// All services with rich SEO content. Each has its own page.
// Service+City combinations are generated dynamically.

export const serviceCategories = [
  {
    slug: "renovatie-verbouwing",
    title: "Renovatie & Verbouwing",
    intro: "Van complete woningrenovaties tot losse vertrekken — strak afgewerkt, op tijd opgeleverd.",
  },
  {
    slug: "uitbouw-constructie",
    title: "Uitbouw & Constructie",
    intro: "Meer ruimte, meer licht, meer waarde. Aanbouw, dakkapel of zolderverbouwing.",
  },
  {
    slug: "kozijnen-gevel",
    title: "Kozijnen & Gevel",
    intro: "Energiezuinige kozijnen, schuifpuien en strakke gevelrenovaties.",
  },
  {
    slug: "afwerking",
    title: "Afwerking",
    intro: "De laatste 10% bepaalt 90% van het resultaat. Stucwerk, tegels, vloeren en schilderwerk.",
  },
  {
    slug: "duurzaamheid-isolatie",
    title: "Duurzaamheid & Isolatie",
    intro: "Lagere energierekening, hoger wooncomfort. Spouwmuur-, dak- en vloerisolatie.",
  },
];

const heroPool = {
  living: "https://images.pexels.com/photos/13722886/pexels-photo-13722886.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1600",
  livingDark: "https://images.unsplash.com/photo-1724582586529-62622e50c0b3?crop=entropy&cs=srgb&fm=jpg&w=1600&q=85",
  kitchenDark: "https://images.unsplash.com/photo-1765766601532-90e9b96320c8?crop=entropy&cs=srgb&fm=jpg&w=1600&q=85",
  kitchenIsland: "https://images.pexels.com/photos/18285887/pexels-photo-18285887.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1600",
  bathroom: "https://images.unsplash.com/photo-1644421439741-712c7fde7e95?crop=entropy&cs=srgb&fm=jpg&w=1600&q=85",
  extension: "https://images.unsplash.com/photo-1754063257992-bb9eabdbdd86?crop=entropy&cs=srgb&fm=jpg&w=1600&q=85",
  facade: "https://images.unsplash.com/photo-1503387762-592deb58ef4e?crop=entropy&cs=srgb&fm=jpg&w=1600&q=85",
  plaster: "https://images.unsplash.com/photo-1604709177225-055f99402ea3?crop=entropy&cs=srgb&fm=jpg&w=1600&q=85",
  tile: "https://images.unsplash.com/photo-1600585154526-990dced4db0d?crop=entropy&cs=srgb&fm=jpg&w=1600&q=85",
  insulation: "https://images.unsplash.com/photo-1565183997392-2f6f122e5912?crop=entropy&cs=srgb&fm=jpg&w=1600&q=85",
  windows: "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?crop=entropy&cs=srgb&fm=jpg&w=1600&q=85",
  attic: "https://images.unsplash.com/photo-1600210492486-724fe5c67fb0?crop=entropy&cs=srgb&fm=jpg&w=1600&q=85",
};

// Helper to build a service object with sensible defaults
const make = (s) => ({
  ...s,
  faqs: s.faqs ?? defaultFaqs(s.title),
  process: s.process ?? defaultProcess,
  why: s.why ?? defaultWhy,
});

const defaultProcess = [
  { step: "01", title: "Vrijblijvend gesprek", text: "We bespreken wensen, budget en planning op locatie of digitaal." },
  { step: "02", title: "Ontwerp & offerte", text: "Heldere offerte met materialen, planning en vaste prijs — geen verrassingen." },
  { step: "03", title: "Uitvoering door vakmensen", text: "Eigen team, dagelijkse rapportage en strakke planning van A tot Z." },
  { step: "04", title: "Oplevering", text: "Zorgvuldige oplevering met eindcontrole. Documentatie en garantievoorwaarden worden bij de oplevering overhandigd." },
];

const defaultWhy = [
  "Vast uitvoeringsteam, aangevuld met vertrouwde specialistische partners",
  "Vaste prijsafspraak — wijzigingen altijd vooraf schriftelijk vastgelegd",
  "VCA-gecertificeerde medewerkers",
  "Werkzaam in heel Zuid-Holland",
  "Ruime ervaring in Zuid-Holland",
];

const defaultFaqs = (svc) => [
  {
    q: `Wat kost een ${svc.toLowerCase()} project gemiddeld?`,
    a: `De kosten hangen af van oppervlakte, materialen en bestaande staat van de woning. Wij maken altijd een vaste prijsofferte zodat u precies weet waar u aan toe bent. Vraag een vrijblijvende kostenindicatie aan.`,
  },
  {
    q: `Hoe lang duurt een gemiddeld ${svc.toLowerCase()} traject?`,
    a: `De doorlooptijd verschilt per project en hangt af van de omvang, de voorbereiding en de levertijd van materialen. In het ontwerp- en offertetraject leggen wij de planning met start- en opleverdatum vast, zodat u vooraf precies weet waar u aan toe bent.`,
  },
  {
    q: "Werken jullie met vaste prijsafspraken?",
    a: "Ja. Wij werken altijd met een vaste totaalprijs op basis van een uitgewerkte offerte. Wijzigingen tijdens het project worden vooraf schriftelijk vastgelegd.",
  },
  {
    q: "Welke garantie krijg ik?",
    a: "Wij staan achter de kwaliteit van ons werk. U ontvangt bij oplevering schriftelijke garantievoorwaarden. Daarnaast geldt fabrieksgarantie op gebruikte materialen.",
  },
  {
    q: "Werken jullie in heel Zuid-Holland?",
    a: "Ja, wij zijn actief in onder andere Rotterdam, Den Haag, Delft, Leiden, Zoetermeer en Dordrecht. Buiten Zuid-Holland in overleg mogelijk.",
  },
];

export const services = [
  // RENOVATIE & VERBOUWING
  make({
    slug: "complete-woning-renovatie",
    category: "renovatie-verbouwing",
    title: "Complete Woning Renovatie",
    short: "Volledig vernieuwde woning, één partij, vaste prijs",
    keyword: "complete renovatie woning",
    image: heroPool.livingDark,
    intro: "Wij verbouwen uw hele woning tot een tijdloos statement — van constructieve aanpassingen tot het laatste plintje. Eén aanspreekpunt, één planning, één vaste prijs.",    bullets: ["Constructieve aanpassingen", "Nieuwe indeling & afwerking", "Inclusief installaties", "Project-management van A–Z"],
  }),
  make({
    slug: "badkamer-renovatie",
    category: "renovatie-verbouwing",
    title: "Badkamer Renovatie",
    short: "Complete badkamer verbouwen door één vast team — in circa twee weken",
    keyword: "badkamer renovatie",
    seoTitle: "Badkamer Renovatie & Verbouwen | Vast team, vaste prijs",
    seoDescription: "Badkamer laten verbouwen in Zuid-Holland? Eigen loodgieter, elektricien en tegelzetter in één vast team. Vaste prijs na gratis opname, uitvoering in ca. 2 weken.",
    image: heroPool.bathroom,
    intro: "Van sloop en loodgieterswerk tot tegels en verlichting — wij verbouwen uw complete badkamer met één vast team van eigen vakmensen. Inloopdouches, vloerverwarming, designkranen en strak tegelwerk, opgeleverd in gemiddeld twee weken.",
    bullets: [
      "Eigen loodgieter, elektricien en tegelzetter in één vast team — één aanspreekpunt",
      "Vaste prijs na gratis opname bij u thuis, afgestemd op de leeftijd van uw woning",
      "Gemiddelde uitvoering van een standaard badkamer: circa twee weken",
      "Inclusief sloop, leidingwerk, elektra, vloerverwarming, tegelwerk en sanitair",
      "Hoogwaardige merken (Hansgrohe, Villeroy & Boch, Mosa)",
    ],
    why: [
      "Eigen loodgieter, elektricien en tegelzetter — geen wachtende onderaannemers",
      "Vaste totaalprijs na opname; bij oudere woningen leggen we vooraf vast wat inbegrepen is",
      "Korte doorlooptijd: standaard badkamer in circa twee weken",
      "VCA-gecertificeerde medewerkers",
      "Werkzaam in Maassluis, Vlaardingen, Schiedam, Rotterdam, Delft, Westland en Den Haag",
    ],
    faqs: [
      { q: "Wat kost een badkamer renovatie?", a: "Dat hangt af van de oppervlakte, de staat van leidingen en ondergrond, de indeling en het niveau van sanitair en tegels. Daarom noemen wij geen standaardprijzen: na een gratis opname bij u thuis ontvangt u een vaste totaalprijs inclusief sloop, loodgieterswerk, elektra, tegelwerk en sanitair." },
      { q: "Hoe lang duurt een badkamer verbouwing?", a: "Een standaard badkamer voeren wij gemiddeld in circa twee weken uit, van sloop tot oplevering. Omdat onze loodgieter, elektricien en tegelzetter in één vast team werken, wachten de disciplines niet op elkaar. Bij een gewijzigde indeling of grotere badkamer plannen we extra tijd in." },
      { q: "Werken jullie met eigen vakmensen of onderaannemers?", a: "Met een eigen vast team: onze loodgieter, elektricien en tegelzetter werken dagelijks samen. U heeft één aanspreekpunt en één planning." },
      { q: "Is de prijs echt vast, ook bij een oudere woning?", a: "Ja. Bij oudere woningen beoordelen we tijdens de opname de leidingen, afvoer en ondergrond en leggen we vooraf vast wat in de prijs is inbegrepen. Zo weet u waar u aan toe bent voordat we starten." },
      { q: "Kan ik mijn badkamer laten verbouwen zonder de indeling te veranderen?", a: "Zeker. Veel klanten kiezen voor een complete vernieuwing op de bestaande indeling: nieuwe leidingen, tegels, inloopdouche, hangtoilet en vloerverwarming. Dat is vaak de snelste en meest efficiënte route." },
      { q: "Doen jullie ook kleine badkamers?", a: "Ja. Juist in kleine badkamers van 3 tot 5 m² maakt een slimme indeling het verschil: inloopdouche zonder cabine, hangtoilet met inbouwreservoir en een smalle wastafel. Tijdens de opname tekenen we de indeling met u uit." },
    ],
  }),
  make({
    slug: "keuken-renovatie",
    category: "renovatie-verbouwing",
    title: "Keuken Renovatie",
    short: "Maatwerk keukens en complete keukenverbouwingen",
    keyword: "keuken renovatie",
    image: heroPool.kitchenIsland,
    intro: "Wij bouwen uw keuken inclusief tegelwerk, elektra, leidingwerk en eventueel doorbraak naar de tuinkamer. Strak, duurzaam en exact volgens ontwerp.",    bullets: ["Maatwerk keukenontwerp", "Inclusief installaties en doorbraak", "Premium fronten, composiet of natuursteen blad", "Inbouwapparatuur naar keuze"],
    faqs: [
      {
        q: "Wat kost een keuken renovatie project gemiddeld?",
        a: "De kosten hangen af van oppervlakte, materialen en bestaande staat van de woning. Wij maken altijd een vaste prijsofferte zodat u precies weet waar u aan toe bent. Vraag een vrijblijvende kostenindicatie aan.",
      },
      {
        q: "Hoe lang duurt een gemiddeld keuken renovatie traject?",
        a: "Een standaard keukenrenovatie waarbij de keuken op dezelfde positie blijft, duurt gemiddeld 3 tot 5 werkdagen. Bij aanvullende werkzaamheden zoals stucwerk, vloeren, verlaagd plafond of het verleggen van installaties moet rekening worden gehouden met een uitvoeringstermijn van circa 1 tot 2 weken.",
      },
      {
        q: "Werken jullie met vaste prijsafspraken?",
        a: "Ja. Wij werken altijd met een vaste totaalprijs op basis van een uitgewerkte offerte. Wijzigingen tijdens het project worden vooraf schriftelijk vastgelegd.",
      },
      {
        q: "Welke garantie krijg ik?",
        a: "Wij staan achter de kwaliteit van ons werk. U ontvangt bij oplevering schriftelijke garantievoorwaarden. Daarnaast geldt fabrieksgarantie op gebruikte materialen.",
      },
      {
        q: "Werken jullie in heel Zuid-Holland?",
        a: "Ja, wij zijn actief in onder andere Rotterdam, Den Haag, Delft, Leiden, Zoetermeer en Dordrecht. Buiten Zuid-Holland in overleg mogelijk.",
      },
    ],
  }),
  make({
    slug: "toilet-renovatie",
    category: "renovatie-verbouwing",
    title: "Toilet Renovatie",
    short: "Strak en compact — toilet vernieuwen binnen een week",
    keyword: "toilet renovatie",
    image: heroPool.tile,
    intro: "Een nieuw toilet binnen vijf werkdagen. Inclusief hangtoilet, tegelwerk, verlichting en fontein. Een kleine renovatie met grote impact.",    bullets: ["Inclusief hangtoilet en inbouwreservoir", "Tegelwerk vloer en wanden", "Spotjes en designkraan", "Klaar binnen 5 werkdagen"],
  }),
  make({
    slug: "appartement-renovatie",
    category: "renovatie-verbouwing",
    title: "Appartement Renovatie",
    short: "Appartement renoveren met respect voor de VvE",
    keyword: "appartement renovatie",
    image: heroPool.living,
    intro: "Renovatie van appartementen in Rotterdam, Den Haag en omgeving. Wij regelen vergunningen, VvE-afstemming en geluidsisolerende vloeren.",    bullets: ["VvE-afstemming en vergunningen", "Geluidsisolerende vloeropbouw", "Strakke afwerking", "Minimale overlast voor buren"],
  }),
  make({
    slug: "cascorenovatie",
    category: "renovatie-verbouwing",
    title: "Cascorenovatie",
    short: "Tot op het casco gestript en compleet opnieuw opgebouwd",
    keyword: "cascorenovatie",
    image: heroPool.extension,
    intro: "Een woning tot op het casco strippen geeft maximale vrijheid in indeling, isolatie en installaties. Wij verzorgen het complete traject inclusief vergunningen.",    bullets: ["Volledige strip & nieuwe constructie", "Nieuwe isolatie en installaties", "Maximale indelingsvrijheid", "Inclusief vergunningen"],
  }),

  // UITBOUW & CONSTRUCTIE
  make({
    slug: "aanbouw",
    category: "uitbouw-constructie",
    title: "Aanbouw",
    short: "Vergroot uw woning met een doordachte aanbouw",
    keyword: "aanbouw",
    image: heroPool.extension,
    intro: "Een aanbouw geeft uw woning een nieuwe dimensie. Wij verzorgen het complete traject: ontwerp, vergunning, fundering, ruwbouw en strakke afwerking met grote schuifpuien.",    bullets: ["Inclusief vergunning en constructie", "Vergunningsvrij binnen de wettelijke maten (Bblv/Wro)", "Grote schuifpuien en lichtkoepels", "Aansluitend op bestaande woning"],
    faqs: [
      {
        q: "Wat kost een aanbouw project gemiddeld?",
        a: "De kosten hangen af van oppervlakte, materialen en bestaande staat van de woning. Wij maken altijd een vaste prijsofferte zodat u precies weet waar u aan toe bent. Vraag een vrijblijvende kostenindicatie aan.",
      },
      {
        q: "Hoe lang duurt een gemiddeld aanbouwtraject?",
        a: "De uitvoering van een aanbouw duurt gemiddeld 1 tot 2 weken bij prefab bouw en 3 tot 6 weken bij traditionele bouw op locatie. De exacte planning is afhankelijk van de afmetingen, afwerking en eventuele constructieve werkzaamheden. In overleg stellen wij vooraf een duidelijke planning op.",
      },
      {
        q: "Werken jullie met vaste prijsafspraken?",
        a: "Ja. Wij werken altijd met een vaste totaalprijs op basis van een uitgewerkte offerte. Wijzigingen tijdens het project worden vooraf schriftelijk vastgelegd.",
      },
      {
        q: "Welke garantie krijg ik?",
        a: "Wij staan achter de kwaliteit van ons werk. U ontvangt bij oplevering schriftelijke garantievoorwaarden. Daarnaast geldt fabrieksgarantie op gebruikte materialen.",
      },
      {
        q: "Werken jullie in heel Zuid-Holland?",
        a: "Ja, wij zijn actief in onder andere Rotterdam, Den Haag, Delft, Leiden, Zoetermeer en Dordrecht. Buiten Zuid-Holland in overleg mogelijk.",
      },
    ],
  }),
  make({
    slug: "uitbouw",
    category: "uitbouw-constructie",
    title: "Uitbouw",
    short: "Optimale ruimtewinst met een lichte uitbouw",
    keyword: "uitbouw",
    image: heroPool.extension,
    intro: "Een uitbouw aan de achterzijde maakt uw keuken of woonkamer ruimer en lichter. Inclusief constructieve berekeningen en complete afwerking.",    bullets: ["Strakke staalconstructie", "Glaspuien en daklichten", "Inclusief vloer, wanden en plafond", "Vergunningvrij tot 4 m diep mogelijk"],
  }),
  make({
    slug: "dakkapel",
    category: "uitbouw-constructie",
    title: "Dakkapel",
    short: "Plaatsing dakkapel in één dag — meer ruimte op zolder",
    keyword: "dakkapel plaatsen",
    image: heroPool.attic,
    intro: "Een dakkapel verandert uw zolder in een volwaardige slaapkamer of werkkamer. Plaatsing in één dag, geïsoleerd, kunststof of houten afwerking.",    bullets: ["Plaatsing in 1 dag", "HR++ glas en isolatie", "Kunststof of hout buitenafwerking", "Inclusief afwerking binnenzijde"],
  }),
  make({
    slug: "zolder-verbouwing",
    category: "uitbouw-constructie",
    title: "Zolder Verbouwing",
    short: "Zolder ombouwen tot slaapkamer, kantoor of badkamer",
    keyword: "zolder verbouwen",
    image: heroPool.attic,
    intro: "Wij verbouwen uw zolder tot extra slaapkamer, masterbedroom met badkamer of inspirerende werkruimte. Inclusief isolatie, elektra en afwerking.",    bullets: ["Isolatie en gipsplaat afwerking", "Inclusief elektra en verlichting", "Optioneel badkamer of dakkapel", "Vaste vloerafwerking"],
  }),
  make({
    slug: "garage-verbouwing",
    category: "uitbouw-constructie",
    title: "Garage Verbouwing",
    short: "Van garage naar woonkamer, kantoor of slaapkamer",
    keyword: "garage verbouwen",
    image: heroPool.living,
    intro: "Maak van uw garage een volwaardige extra kamer. Inclusief isolatie, vloerverwarming, elektra en kozijnen.",    bullets: ["Isolatie van wanden, vloer en dak", "Vloerverwarming inbegrepen", "Nieuwe kozijnen of pui", "Strakke afwerking"],
  }),
  make({
    slug: "verlaagd-plafond",
    category: "uitbouw-constructie",
    title: "Verlaagd Plafond",
    short: "Strak verlaagd plafond met sfeerverlichting",
    keyword: "verlaagd plafond",
    image: heroPool.livingDark,
    intro: "Een verlaagd plafond met spots en indirecte verlichting transformeert iedere kamer. Inclusief stof- en geluidsisolatie en elektra.",    bullets: ["Inclusief LED spots en bekabeling", "Optioneel indirecte LED verlichting", "Geluids- en isolatieklasse op maat", "Strak afgewerkt met sausklare gipsplaat"],
  }),
  make({
    slug: "cinewall",
    category: "uitbouw-constructie",
    title: "Cinewall op Maat",
    short: "Maatwerk cinewall met sfeerverlichting en haard-optie",
    keyword: "cinewall op maat",
    image: heroPool.livingDark,
    intro: "Een eye-catcher in uw woonkamer. Wij ontwerpen en bouwen uw cinewall op maat — inclusief LED, haard, geluidsbox en kabelmanagement.",    bullets: ["Op maat ontwerp en uitvoering", "Sfeerverlichting en haardopening", "Geïntegreerd geluidsysteem mogelijk", "Inclusief kabelmanagement"],
  }),

  // KOZIJNEN & GEVEL
  make({
    slug: "kunststof-kozijnen",
    category: "kozijnen-gevel",
    title: "Kunststof Kozijnen",
    short: "Onderhoudsvrije kunststof kozijnen met HR++ glas",
    keyword: "kunststof kozijnen",
    image: heroPool.windows,
    intro: "Wij leveren en plaatsen kwaliteitskozijnen van A-merken. Onderhoudsvrij, hoog isolerend en KOMO-gecertificeerd.",    bullets: ["KOMO-keurmerk", "HR++ glas standaard, triple optioneel", "Vele kleuren en houtnerf-uitvoeringen", "Onderhoudsvrij"],
  }),
  make({
    slug: "aluminium-kozijnen",
    category: "kozijnen-gevel",
    title: "Aluminium Kozijnen",
    short: "Slanke aluminium profielen voor grote glaspuien",
    keyword: "aluminium kozijnen",
    image: heroPool.windows,
    intro: "Aluminium kozijnen zijn de premium keuze: slanke profielen, maximaal lichtinval en strak design. Ideaal voor schuifpuien en grote raampartijen.",    bullets: ["Ultra-slanke profielen", "Triple glas mogelijk", "Inbraakwerend WK2", "Ral-kleur naar wens"],
  }),
  make({
    slug: "schuifpuien",
    category: "kozijnen-gevel",
    title: "Schuifpuien",
    short: "Grote schuifpuien — naadloze overgang naar de tuin",
    keyword: "schuifpui plaatsen",
    image: heroPool.windows,
    intro: "Maximaal licht en een vloeiende overgang naar uw tuin. Wij plaatsen aluminium en kunststof schuifpuien tot 6 meter breed.",    bullets: ["Tot 6 meter breed", "Inbraakwerend glas", "Hoogwaardig hang- en sluitwerk", "Inclusief plaatsing en afwerking"],
  }),
  make({
    slug: "voordeuren",
    category: "kozijnen-gevel",
    title: "Voordeuren",
    short: "Inbraakwerende designvoordeuren op maat",
    keyword: "voordeur op maat",
    image: heroPool.facade,
    intro: "Uw voordeur is uw visitekaartje. Wij leveren en plaatsen designvoordeuren in aluminium of kunststof, voorzien van inbraakwerend hang- en sluitwerk.",    bullets: ["SKG*** inbraakwerend", "Aluminium of kunststof", "Glaselementen op maat", "Optioneel met vingerprint of code"],
  }),
  make({
    slug: "gevelrenovatie",
    category: "kozijnen-gevel",
    title: "Gevelrenovatie",
    short: "Voeg- en metselwerk, gevelreiniging en isolatie",
    keyword: "gevelrenovatie",
    image: heroPool.facade,
    intro: "Wij vernieuwen voegen, repareren metselwerk en reinigen of stucen uw gevel. Het resultaat: een sterk verbeterde uitstraling én isolatiewaarde.",    bullets: ["Uithakken en opnieuw voegen", "Hydrofoberen of gevelreiniging", "Optioneel buitengevelisolatie", "Steigerwerk inbegrepen"],
  }),

  // AFWERKING
  make({
    slug: "stukadoor",
    category: "afwerking",
    title: "Stukadoor",
    short: "Strakke wanden en plafonds — sausklaar of decoratief",
    keyword: "stukadoor",
    image: heroPool.plaster,
    intro: "Onze stukadoors leveren spiegelglad sausklaar stucwerk én decoratieve technieken als beton-ciré en kalkverf. Ook voor sierpleister buiten.",    bullets: ["Sausklaar of decoratief", "Beton-ciré specialisme", "Sierpleister buiten", "Strak afgekitte aansluitingen"],
  }),
  make({
    slug: "schilderwerk",
    category: "afwerking",
    title: "Schilderwerk",
    short: "Binnen- en buitenschilderwerk door vakschilders",
    keyword: "schilderwerk",
    image: heroPool.plaster,
    intro: "Schilderwerk binnen of buiten — wij gebruiken hoogwaardige verven van Sikkens en Sigma, met een lange dekking en garantie.",    bullets: ["Sikkens / Sigma kwaliteit", "Inclusief schuren en plamuren", "Binnen- en buitenschilderwerk", "Tot 6 jaar garantie op buitenwerk"],
  }),
  make({
    slug: "tegelwerk",
    category: "afwerking",
    title: "Tegelwerk",
    short: "Vakkundig tegelwerk voor badkamer, vloer en gevel",
    keyword: "tegelwerk",
    image: heroPool.tile,
    intro: "Wij plaatsen wand- en vloertegels in elk formaat — inclusief groot formaat (120×60 of 100×100), visgraat en mozaïek. Strak gevoegd, perfect uitgelijnd.",    bullets: ["Groot formaat tegels (XXL)", "Visgraat en mozaïek patronen", "Strakke voegen met epoxy optie", "Vloer-, wand- en buitentegels"],
  }),
  make({
    slug: "pvc-vloeren",
    category: "afwerking",
    title: "PVC Vloeren",
    short: "Premium PVC en click-vloeren met vloerverwarming",
    keyword: "pvc vloer leggen",
    image: heroPool.living,
    intro: "PVC is dé moderne vloer: warm, geluiddempend en perfect met vloerverwarming. Wij egaliseren, leveren en leggen volgens fabrieksvoorschrift.",    bullets: ["Inclusief egalisatie", "Geschikt voor vloerverwarming", "Visgraat patronen mogelijk", "Strak afgekit met plinten"],
  }),
  make({
    slug: "laminaat-leggen",
    category: "afwerking",
    title: "Laminaat Leggen",
    short: "Snel en strak gelegd laminaat met geluidsondervloer",
    keyword: "laminaat leggen",
    image: heroPool.living,
    intro: "Wij leggen laminaat strak en geluidsdempend — inclusief ondervloer en plinten. Standaard en visgraat patronen.",    bullets: ["Inclusief geluidsondervloer", "Plinten naar keuze", "Visgraat optioneel", "Snelle oplevering"],
  }),

  // DUURZAAMHEID & ISOLATIE
  make({
    slug: "spouwmuurisolatie",
    category: "duurzaamheid-isolatie",
    title: "Spouwmuurisolatie",
    short: "Direct besparen op uw stookkosten",
    keyword: "spouwmuurisolatie",
    image: heroPool.insulation,
    intro: "Spouwmuurisolatie levert direct rendement: tot 20% lagere energierekening. Wij blazen EPS-parels in volgens KOMO-norm.",    bullets: ["KOMO en SKG keurmerk", "Subsidie aanvraag inbegrepen", "Direct rendement", "Geen overlast"],
  }),
  make({
    slug: "dakisolatie",
    category: "duurzaamheid-isolatie",
    title: "Dakisolatie",
    short: "Beter wooncomfort en flink lagere energiekosten",
    keyword: "dakisolatie",
    image: heroPool.insulation,
    intro: "Dakisolatie van binnen of buitenuit — wij rekenen Rc-waardes door en helpen met de subsidieaanvraag.",    bullets: ["Binnen- of buitenisolatie", "Rc tot 6,3", "Subsidieaanvraag inbegrepen", "Strak afgewerkt"],
  }),
  make({
    slug: "vloerisolatie",
    category: "duurzaamheid-isolatie",
    title: "Vloerisolatie",
    short: "Koude voeten verleden tijd — direct merkbaar",
    keyword: "vloerisolatie",
    image: heroPool.insulation,
    intro: "PUR-schuim of EPS-platen onder uw begane grond vloer. Resultaat: een warmer huis, lagere stookkosten en directe ROI.",    bullets: ["PUR of EPS", "Rc tot 5,0", "ISDE-subsidie mogelijk (i.c.m. 2e maatregel)", "Meestal in één werkdag aangebracht"],
  }),
  make({
    slug: "geluidsisolatie",
    category: "duurzaamheid-isolatie",
    title: "Geluidsisolatie",
    short: "Stilte in elk vertrek — wand, vloer en plafond",
    keyword: "geluidsisolatie",
    image: heroPool.plaster,
    intro: "Last van buren of straatlawaai? Wij adviseren en plaatsen geluidsisolerende systemen voor wanden, vloeren en plafonds.",    bullets: ["Voor- en nameting mogelijk", "Tot 12 dB reductie", "Wand, vloer en plafond", "Strakke afwerking"],
  }),
  make({
    slug: "gevelisolatie",
    category: "duurzaamheid-isolatie",
    title: "Gevel isolatie",
    short: "Binnen-gevelisolatie — direct warmer wonen en lagere energiekosten",
    keyword: "gevel isolatie binnen",
    image: heroPool.insulation,
    intro: "Binnen-gevelisolatie is dé oplossing wanneer spouwmuur- of buitengevelisolatie niet mogelijk is — bijvoorbeeld bij monumenten, appartementen of woningen zonder spouw. Wij plaatsen hoogwaardige isolatieplaten aan de binnenzijde van uw gevel, strak afgewerkt met gipsplaat en sausklaar opgeleverd. Direct merkbaar meer comfort, minder tocht en een fors lagere energierekening.",    bullets: [
      "Ideaal bij monumenten, appartementen en woningen zonder spouw",
      "Rc-waarde tot 5,0 mogelijk",
      "Dampremmende laag tegen vocht en schimmel",
      "Strak afgewerkt met gipsplaat, sausklaar opgeleverd",
      "Subsidieaanvraag (ISDE) inbegrepen",
    ],
  }),
  make({
    slug: "hr-plus-glas",
    category: "duurzaamheid-isolatie",
    title: "HR++ Glas",
    short: "HR++ of triple glas — comfort en isolatie omhoog",
    keyword: "hr++ glas",
    image: heroPool.windows,
    intro: "Vervang enkel of dubbel glas door HR++ of triple. Direct minder kou langs ramen en lagere stookkosten.",    bullets: ["HR++ standaard, triple optioneel", "Inclusief plaatsing en kit", "Subsidie aanvraag inbegrepen", "10 jaar fabrieksgarantie"],
  }),
];

export const findService = (slug) => services.find((s) => s.slug === slug);
export const servicesByCategory = (cat) => services.filter((s) => s.category === cat);

// Precomputed once at module load: avoids filter() in render paths (Navbar, HomePage).
export const servicesGrouped = Object.fromEntries(
  serviceCategories.map((c) => [c.slug, services.filter((s) => s.category === c.slug)])
);
